import { useState, useEffect, useRef } from 'react';
import Logger from 'services/debug/logger';
import useWhyDidYouUpdate from 'hooks/useWhyDidYouUpdate';
// import workletUrl from 'services/webRTC/audioMeter.worklet';
import workletUrl from 'services/webRTC/speakingMeter.worklet';
// import useDebounce from 'react-use/lib/useDebounce';
// import useThrottleFn from 'react-use/lib/useThrottleFn';
import useMountedState from 'react-use/lib/useMountedState';

const logger = new Logger('component:useSoundMeter');

const AudioContext = window.AudioContext || window.webkitAudioContext;

const HISTORY_ROTATION_COUNT = 8;

export default ({ track, precision, refreshRate, isMe, isTrackMuted, _client }) => {
    const [strength, setStrength] = useState();
    const [, setModuleLoaded] = useState();
    const audioContext = useRef();
    const node = useRef();
    const [error, setError] = useState();
    const [warning, setWarning] = useState();
    const [muted, setMuted] = useState();
    const isMounted = useMountedState();
    const [speaking, setSpeaking] = useState(false);

    useEffect(() => {
        if (!AudioContext && isMe) {
            setError('room.error.webAudioNotSupported');
        }
    }, []);

    // const [isReady, cancelDebounce] = useDebounce(
    //     (value) => {
    //         // console.log('###setSignalEmpty:debounced ', isReady(), isMounted);
    //         if (isMounted && !strength) {
    //             // setStrength(0);
    //             setSpeaking(false)
    //         }
    //     },
    //     1000,
    //     [strength]
    // );

    // const value = useThrottleFn(
    //     (value) => value === 0,
    //     3000,
    //     [strength]
    //     );
    //     console.log('VVvalue: ', value);

    useWhyDidYouUpdate('component:SoundMeterStrength:useSoundMeter', { track, precision, refreshRate, audioContext: audioContext.current, error, isTrackMuted });

    const processDataSpeaking = (event) => {
        const { isSpeaking, strength: volume, isMuted } = event.data;

        // logger.log('########useSoundMeter:useeffect:processDataSpeaking', isSpeaking, event.data);
        if (audioContext.current?.state === 'running' && track?.id) {
            // setStrength(isSpeaking ? 1 : 0);
            setSpeaking(isSpeaking);
            setStrength(Math.min(Math.round(volume), 10));
            // const newStrengthHistory = takeRight([ ...strengthHistory.current, isMuted], HISTORY_ROTATION_COUNT);

            setMuted(isMuted);

            if (!isTrackMuted && isMuted) {
                if(isMe){
                    setError('room.error.emptyAudio.me');
                } else {
                    setWarning('room.error.emptyAudio')
                }
            } else {
                setError(undefined);
                setWarning(undefined);
            }
        }
    };

    const loadModule = async () => {
        try {
            await audioContext.current.audioWorklet.addModule(workletUrl);
            setModuleLoaded({ moduleLoaded: true });
            logger.log(`loaded module ${workletUrl}`);
        } catch(e) {
            setModuleLoaded({ moduleLoaded: false });
            setError('room.error.cannotAnalyseStream');
            logger.warn(`Failed to load module ${workletUrl}`);
        }
    }

    const initAudioContext = async (t) => {
        // Create the Audio Context
        const stream = new MediaStream([t]);

        try {
            logger.log('New context instantiated')
            audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch(e) {
            logger.warn(`Sorry, but your browser doesn't support the Web Audio API!`, e);
            if (isMe) setError('room.error.audioContextNotSupported');
        }

        logger.log('###audioContext.create: ');
        const source = audioContext.current.createMediaStreamSource(stream);

        const processorOptions = { 
            updateIntervalInMS: refreshRate, 
            debug: false, 
            maxHistorySize: HISTORY_ROTATION_COUNT, 
            _client 
        };
        // load worklet, and load it if not loaded before
        try {
            node.current = new AudioWorkletNode(audioContext.current, 'speakingMeter', { processorOptions });
        }
        catch (e) {
            // Load the worklet
            await loadModule();
            node.current = new AudioWorkletNode(audioContext.current, 'speakingMeter', { processorOptions });
        }

        node.current.port.onmessage = processDataSpeaking;

        // Connect the audio pipeline - this will start the processing
        source.connect(node.current) // .connect(audioContext.current.destination);
    }

    const cleanAudioContext = () => {
        logger.log('cleanAudioContext:close:state: ', audioContext.current);
        if (audioContext.current) {
            // node.current.port.postMessage({ isPlaying: false });
            // const isPlaying = node.current?.parameters.get('isPlaying');
            // isPlaying.value = false;
            if (audioContext.current.state !== 'closed') {
                audioContext.current.close();
            }
            audioContext.current = undefined;
        }
        if (isMounted) {
            setSpeaking(false);
            setStrength(0);
        }
    }

    useEffect(() => {
        logger.log('####audioContext.current?.state: ', track, audioContext.current?.state);
        setError(undefined);
        setWarning(undefined);
        if (!isTrackMuted && track && !audioContext.current) {
            initAudioContext(track);
        }
        else if ((isTrackMuted || !track) && audioContext.current) {
            cleanAudioContext();
        }

        return cleanAudioContext;
    }, [track, isTrackMuted]);

    useEffect(() => {
        logger.log('useEffect:isTrackMuted', isTrackMuted, audioContext.current?.state);
        if (isTrackMuted && audioContext.current && audioContext.current?.state !== 'suspended') {
            logger.log('useEffect:isTrackMuted: context suspended');
            // node.current?.port.postMessage({ isPlaying: false });
            // const isPlaying = node.current?.parameters.get('isPlaying');
            // isPlaying.value = false;
            audioContext.current.suspend();
            setSpeaking(false);
            setStrength(0);
        } else if (!isTrackMuted && audioContext.current && audioContext.current?.state !== 'running') {
            logger.log('useEffect:isTrackMuted: context resumed');
            // node.current?.port.postMessage({ isPlaying: true });
            // const isPlaying = node.current?.parameters.get('isPlaying');
            // isPlaying.value = true;
            audioContext.current.resume();
        }

    }, [isTrackMuted]);

    return [strength, muted, error, speaking, warning];
};
